import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, ArrowRight } from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import { modules } from "@/data/modules";
import { industries } from "@/data/industries";
import { trackAdsConversion } from "@/lib/trackAdsConversion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empRanges = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];
const customChoices = ["No", "Minor changes", "Significant customization", "Not sure yet"];

export default function Contact() {
  const [sp] = useSearchParams();
  const initialIntent = sp.get("intent") || "book_demo";
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    company_name: "", name: "", email: "", phone: "",
    industry: "", employee_count: "", current_hr_software: "",
    customization_required: "", message: "", interested_modules: [],
    intent: initialIntent,
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleModule = (name) => {
    const list = form.interested_modules.includes(name)
      ? form.interested_modules.filter((m) => m !== name)
      : [...form.interested_modules, name];
    setForm({ ...form, interested_modules: list });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackAdsConversion();
      toast.success("Thanks! Our team will reach out within 1 business day.");
      nav("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const quickAction = (intent) => setForm({ ...form, intent });

  return (
    <div data-testid="page-contact">
      <section className="py-20 hero-grid-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-4">Contact</div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-[#1F1E4E] leading-[1.05]">
            Let's talk about your HR workflows.
          </h1>
          <p className="mt-5 text-lg text-slate-600">Fill in the form. An HR specialist will reach out within 1 business day.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-[#1F1E4E] rounded-2xl p-6 text-white">
              <div className="text-xs font-bold uppercase tracking-widest text-[#00A651] mb-4">Quick Actions</div>
              <div className="space-y-2">
                {[
                  { k: "book_demo", label: "Book a Demo", tid: CONTACT.bookDemo },
                  { k: "request_quote", label: "Request a Quote", tid: CONTACT.requestQuote },
                  { k: "contact_sales", label: "Contact Sales" },
                ].map((a) => {
                  const Ic = ArrowRight;
                  return (
                    <button
                      key={a.k}
                      type="button"
                      data-testid={a.tid}
                      onClick={() => quickAction(a.k)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-bold transition-all ${
                        form.intent === a.k
                          ? "bg-[#00A651] border-[#00A651] text-white"
                          : "border-white/20 text-white/90 hover:bg-white/5"
                      }`}
                    >
                      {a.label} <Ic className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
              <div className="text-sm font-bold text-[#1F1E4E]">Prefer to reach out directly?</div>
              <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="contact-phone"><Phone className="h-4 w-4 text-[#00A651]" /> <a href="tel:+917976117684" className="hover:text-[#1F1E4E] font-semibold">+91-7976117684</a></div>
              <a
                href="https://wa.me/917976117684?text=Hi%20Clocked%20In%20One%2C%20I%27d%20like%20to%20know%20more%20about%20your%20workforce%20management%20platform."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1FB955] text-white font-bold rounded-lg px-4 py-2.5 text-sm transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true"><path d="M20.52 3.48A11.87 11.87 0 0 0 12.05 0C5.5 0 .17 5.33.17 11.88c0 2.1.55 4.14 1.6 5.94L0 24l6.34-1.66a11.86 11.86 0 0 0 5.7 1.45h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.24-6.15-3.41-8.43ZM12.05 21.5h-.01a9.6 9.6 0 0 1-4.9-1.34l-.35-.21-3.76.98 1-3.67-.23-.38a9.62 9.62 0 0 1-1.47-5.1c0-5.31 4.33-9.63 9.65-9.63 2.58 0 5 1 6.82 2.83a9.6 9.6 0 0 1 2.83 6.82c0 5.31-4.33 9.7-9.63 9.7Zm5.55-7.19c-.3-.15-1.79-.88-2.07-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.35.22-.65.08-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.5-1.78-1.68-2.08-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.68-1.64-.93-2.24-.24-.58-.5-.5-.68-.51h-.58c-.2 0-.53.08-.8.38-.28.3-1.06 1.04-1.06 2.53s1.08 2.94 1.23 3.14c.15.2 2.14 3.28 5.19 4.6.72.32 1.28.5 1.72.65.72.23 1.38.2 1.9.12.58-.09 1.79-.73 2.04-1.44.25-.7.25-1.31.18-1.44-.08-.13-.28-.2-.58-.35Z"/></svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>

          <form data-testid={CONTACT.form} onSubmit={submit} className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 p-8">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Company Name *"><input required data-testid={CONTACT.company} value={form.company_name} onChange={set("company_name")} className={inputCls} /></Field>
              <Field label="Your Name *"><input required data-testid={CONTACT.name} value={form.name} onChange={set("name")} className={inputCls} /></Field>
              <Field label="Work Email *"><input required type="email" data-testid={CONTACT.email} value={form.email} onChange={set("email")} className={inputCls} /></Field>
              <Field label="Phone *"><input required data-testid={CONTACT.phone} value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
              <Field label="Industry">
                <select data-testid={CONTACT.industry} value={form.industry} onChange={set("industry")} className={inputCls}>
                  <option value="">Select industry</option>
                  {industries.map((i) => <option key={i.slug} value={i.name}>{i.name}</option>)}
                </select>
              </Field>
              <Field label="Employee Count">
                <select data-testid={CONTACT.employees} value={form.employee_count} onChange={set("employee_count")} className={inputCls}>
                  <option value="">Select size</option>
                  {empRanges.map((r) => <option key={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Current HR Software"><input data-testid={CONTACT.currentSoftware} value={form.current_hr_software} onChange={set("current_hr_software")} className={inputCls} placeholder="e.g., Spreadsheets, BambooHR" /></Field>
              <Field label="Customization Required">
                <select data-testid={CONTACT.customization} value={form.customization_required} onChange={set("customization_required")} className={inputCls}>
                  <option value="">Select</option>
                  {customChoices.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <div className="mt-5">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Interested Modules</div>
              <div className="flex flex-wrap gap-2">
                {modules.map((m) => {
                  const active = form.interested_modules.includes(m.name);
                  return (
                    <button
                      type="button"
                      key={m.slug}
                      onClick={() => toggleModule(m.name)}
                      data-testid={`module-chip-${m.slug}`}
                      className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${
                        active
                          ? "bg-[#00A651] text-white border-[#00A651]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#00A651]"
                      }`}
                    >
                      {m.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <Field label="Message" className="mt-5">
              <textarea data-testid={CONTACT.message} rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your team and workflow needs..." />
            </Field>

            <button type="submit" disabled={submitting} data-testid={CONTACT.submit} className="mt-6 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008A43] disabled:opacity-60 text-white font-bold rounded-lg px-6 py-3 transition-colors">
              {submitting ? "Submitting..." : `Submit — ${labelFor(form.intent)}`} <ArrowRight className="h-4 w-4" />
            </button>
            <div className="mt-3 text-xs text-slate-500">By submitting, you agree to our Privacy Policy.</div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#00A651] focus:border-transparent outline-none transition-all";

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function labelFor(intent) {
  return { book_demo: "Book Demo", request_quote: "Request Quote", contact_sales: "Contact Sales" }[intent] || "Submit";
}
